import { makeAutoObservable } from "mobx"
import styles from "./styles.module.scss"
import { observer } from "mobx-react-lite"
import { useEffect, useMemo } from "react"
import classNames from "classnames"
import { Tooltip } from "../../../../atoms/Tooltip"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import AlertCircleOutline from "~icons/eva/alert-circle-outline"
import Edit2Outline from "~icons/eva/edit-2-outline"
import Trash2Outline from "~icons/eva/trash-2-outline"
import PlusOutline from "~icons/eva/plus-outline"
import { colors } from "@theme/colors"
import { FormPhoneInput } from "../../../../atoms/FormPhoneInput"
import { Control, useForm } from "react-hook-form"
import { FormSelectInput } from "../../../../atoms/FormSelectInput"
import { getStates, getUserTitle } from "@cruisen/react-shared"
import FormTextInput from "../../../../atoms/FormTextInput"
import { defaultCountries } from "react-international-phone"
import { Popover } from "../../../../atoms/Popover"
import { CountriesList } from "../../../../atoms/CountriesList"
import { StatesList } from "../../../../atoms/StatesList"
import { FormDatePicker } from "../../../../atoms/FormDatePicker"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useStores } from "../../../../../models/helpers/useStores"
import SelectAgeInput from "../../../../atoms/SelectAgeInput"
import { CruisePassengerAgeGroup } from "@cruisen/contract"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { CountryPickerInput } from "../../../../atoms/FormCountryInput"
import { CountyPickerInput } from "../../../../atoms/FormCountyInput"

class DataGridItemStore {
  isCopied = false

  constructor() {
    makeAutoObservable(this)
  }

  setCopied(copied: boolean) {
    this.isCopied = copied
  }
}

export type TDataGridItem = {
  label: string
  value: string
  optional?: boolean
  copyDisabled?: boolean
  copyValue?: string
  formName?: string
  formValue?: string
}

const DataGridItem = observer(({ item }: { item: TDataGridItem }) => {
  const store = useMemo(() => new DataGridItemStore(), [])

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value)
    store.setCopied(true)

    setTimeout(() => {
      store.setCopied(false)
    }, 750)
  }

  const itemId = (item.label || "").split(" ").join("-").toLowerCase()
  const className = classNames(styles.value, item.value.length > 30 && styles.longValue, itemId)

  if (item.copyDisabled) {
    return <p className={className}>{item.value}</p>
  }

  return (
    <>
      <p
        onClick={() => copyToClipboard(item.copyValue ?? item.value)}
        className={className}
        data-tooltip-id={itemId}
        data-tooltip-content="Copied!"
      >
        {item.value}
      </p>
      <Tooltip id={itemId} isOpen={store.isCopied} place="bottom" />
    </>
  )
})

class DataGridStore {
  isEditMode = false
  activeModal = null

  constructor() {
    makeAutoObservable(this)
  }

  setEditMode(editMode: boolean) {
    this.isEditMode = editMode
  }

  setActiveModal(activeModal: string | null) {
    this.activeModal = activeModal
  }
}

export const DataGrid = observer(
  ({
    icon,
    title,
    data,
    onDataReveal,
    onEdit,
    defaultFormValues = {},
    emptyStateText,
    cruiseLineLoyalty = false,
    openDeleteLoyaltyNumberModal,
    openAddLoyaltyNumberModal,
    resolver,
    forceEdit = false,
    parentControl,
    parentSetValue,
    parentGetValues,
  }: {
    icon: React.ReactNode
    title: string
    data: TDataGridItem[]
    onDataReveal?: () => void
    onEdit?: (formValues: Record<string, string>) => void
    defaultFormValues?: Record<string, string>
    emptyStateText?: string
    cruiseLineLoyalty?: boolean
    openDeleteLoyaltyNumberModal?: (formValue: string) => void
    openAddLoyaltyNumberModal?: () => void
    resolver?: yup.ObjectSchema<Record<string, string>>
    forceEdit?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parentControl?: Control<any, any>
    parentSetValue?: (name: string, value: string) => void
    parentGetValues?: (name: string) => string
  }) => {
    const rootStore = useStores()
    const store = useMemo(() => new DataGridStore(), [])
    const emptyFields = data.filter((item) => !item.value && !item.optional)

    const {
      control,
      handleSubmit,
      setValue,
      watch,
      getValues,
      reset,
      formState: { isValid },
    } = useForm({
      mode: "all",
      defaultValues: defaultFormValues,
      resolver: resolver ? yupResolver(resolver) : undefined,
    })

    const queryClient = useQueryClient()

    const { mutate: submitForm, isPending: isSubmitting } = useMutation({
      mutationFn: handleSubmit(onEdit),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["allProfiles", rootStore.activeTrip?.id] })
        store.setEditMode(false)
      },
      onError: () => {
        reset(defaultFormValues)
        alert("Error editing guest please try again.")
      },
    })

    const containsFirstName = data.some((item) => item.formName === "firstName")
    const containsLastName = data.some((item) => item.formName === "lastName")

    const [country, firstName, lastName, ageGroup] = watch([
      "country",
      "firstName",
      "lastName",
      "ageGroup",
    ])

    const setFormValue = (name: string, value: string) => {
      if (parentSetValue) {
        parentSetValue(name, value)
      } else {
        setValue(name, value)
      }
    }

    const getFormValues = (name: string) => {
      if (parentControl) {
        return parentGetValues(name)
      }
      return getValues(name)
    }

    useEffect(() => {
      if (defaultFormValues?.email) {
        setFormValue("email", defaultFormValues.email)
      }
    }, [defaultFormValues?.email])

    const formControl = parentControl || control

    const isSaveDisabled =
      (containsFirstName && !firstName) || (containsLastName && !lastName) || !isValid

    return (
      <div className={styles.detailsSection}>
        <div className={styles.dataGridHeader}>
          <div className={styles.dataGridHeaderIcons}>
            {icon}
            <p className={styles.dataGridTitle}>{title}</p>
          </div>
          {onEdit && !store.isEditMode && (
            <button
              className={styles.editIconWrapper}
              onClick={() => {
                if (data.length === 0) {
                  store.setEditMode(false)
                  openAddLoyaltyNumberModal()
                } else {
                  reset(defaultFormValues)
                  store.setEditMode(true)
                }
              }}
            >
              {data.length === 0 ? (
                <>
                  <PlusOutline className={styles.editIcon} />
                  <p className={styles.editIconText}>Add</p>
                </>
              ) : (
                <>
                  <Edit2Outline className={styles.editIcon} />
                  <p className={styles.editIconText}>Edit</p>
                </>
              )}
            </button>
          )}
        </div>
        <div
          className={classNames(
            styles.grid,
            onEdit ? styles.tripleGrid : forceEdit ? styles.singleGrid : styles.doubleGrid,
            store.isEditMode && cruiseLineLoyalty ? styles.gridWithAddButton : "",
          )}
        >
          {data.map((item) =>
            item.formName === "county" && country && country !== "US" ? null : (
              <div key={item.label} className={styles.gridItem}>
                {!store.isEditMode && !forceEdit && (
                  <p
                    className={classNames(
                      styles.label,
                      !item.value && !item.optional && styles.emptyValueLabel,
                    )}
                  >
                    {item.label}
                  </p>
                )}
                {item.value === "%skeleton%" ? (
                  <Skeleton containerClassName={styles.skeletonContainer} />
                ) : store.isEditMode || forceEdit ? (
                  item.formName === "phone" ? (
                    <FormPhoneInput
                      control={formControl}
                      name={item.formName}
                      label={item.label}
                      parentBackgroundColor={colors.backgroundSecondary}
                    />
                  ) : item.formName === "title" ? (
                    <FormSelectInput
                      control={formControl}
                      name={item.formName}
                      options={getUserTitle()}
                      label={item.label}
                      parentBackgroundColor={colors.backgroundSecondary}
                    />
                  ) : item.formName === "county" ? (
                    <>
                      {forceEdit ? (
                        <CountyPickerInput
                          control={formControl}
                          name={item.formName}
                          label={item.label}
                          parentBackgroundColor={colors.backgroundSecondary}
                        />
                      ) : (
                        <Popover
                          isOpen={store.activeModal === item.formName}
                          onClickOutside={() => store.setActiveModal(null)}
                          content={
                            <StatesList
                              onSelect={(value) => {
                                setFormValue(item.formName, value)
                                store.setActiveModal(null)
                              }}
                              onClose={() => store.setActiveModal(null)}
                            />
                          }
                        >
                          <div className={styles.countryInput}>
                            <FormTextInput
                              control={formControl}
                              name={item.formName}
                              label={item.label}
                              value={
                                getStates().find(
                                  (state) => state.value === getFormValues(item.formName),
                                )?.name
                              }
                              onClick={() => store.setActiveModal(item.formName)}
                              readOnly
                              parentBackgroundColor={colors.backgroundSecondary}
                            />
                          </div>
                        </Popover>
                      )}
                    </>
                  ) : item.formName === "ageGroup" ? (
                    <SelectAgeInput
                      control={formControl}
                      name={item.formName}
                      label={item.label}
                      parentBackgroundColor={colors.backgroundSecondary}
                    />
                  ) : item.formName === "nationality" ||
                    item.formName === "country" ||
                    item.formName === "passportIssuingCountry" ? (
                    <>
                      {forceEdit ? (
                        <CountryPickerInput
                          control={formControl}
                          name={item.formName}
                          label={item.label}
                          parentBackgroundColor={colors.backgroundSecondary}
                        />
                      ) : (
                        <Popover
                          isOpen={store.activeModal === item.formName}
                          onClickOutside={() => store.setActiveModal(null)}
                          content={
                            <CountriesList
                              withPhonePrefix={false}
                              onSelect={(iso2) => {
                                setFormValue(item.formName, iso2.toUpperCase())
                                store.setActiveModal(null)
                              }}
                              onClose={() => store.setActiveModal(null)}
                            />
                          }
                        >
                          <div className={styles.countryInput}>
                            <FormTextInput
                              control={formControl}
                              name={item.formName}
                              label={item.label}
                              value={
                                defaultCountries.find(
                                  (country) =>
                                    country[1].toUpperCase() === getFormValues(item.formName),
                                )?.[0]
                              }
                              onClick={() => store.setActiveModal(item.formName)}
                              readOnly
                              parentBackgroundColor={colors.backgroundSecondary}
                            />
                          </div>
                        </Popover>
                      )}{" "}
                    </>
                  ) : item.formName === "dob" ||
                    item.formName === "passportExpireDate" ||
                    item.formName === "passportIssueDate" ? (
                    <FormDatePicker
                      control={formControl}
                      inputName={item.formName}
                      label={item.label}
                      parentBackgroundColor={colors.backgroundSecondary}
                      onChange={(date) => setFormValue(item.formName, date)}
                      defaultDate={item.formValue || item.value || ""}
                      inOrder={item.formName === "passportExpireDate"}
                      isDisabled={false}
                      forceEdit={forceEdit}
                      ageGroup={
                        item.formName === "dob" ? (ageGroup as CruisePassengerAgeGroup) : undefined
                      }
                      enabledDateRange={{
                        minDate:
                          item.formName === "passportExpireDate"
                            ? new Date()
                            : new Date("1900-01-01"),
                        maxDate:
                          // Passports are valid for 10 years give extra 5 years for buffer
                          item.formName === "passportExpireDate"
                            ? new Date(
                                new Date().getFullYear() + 15,
                                new Date().getMonth(),
                                new Date().getDate(),
                              )
                            : new Date(),
                      }}
                      selectMonthYearDropdown={true}
                    />
                  ) : (
                    <FormTextInput
                      control={formControl}
                      name={item.formName}
                      disabled={item.formName === "email" && !forceEdit}
                      label={item.label}
                      parentBackgroundColor={
                        item.formName === "email" && !forceEdit
                          ? colors.backgroundTertiary
                          : colors.backgroundSecondary
                      }
                      iconRight={
                        item.formName.startsWith("cruiseLine+") ? (
                          <button
                            className={styles.deleteButton}
                            onClick={() => {
                              store.setEditMode(false)
                              openDeleteLoyaltyNumberModal(item.formName)
                            }}
                          >
                            <Trash2Outline className={styles.deleteIcon} />
                          </button>
                        ) : null
                      }
                    />
                  )
                ) : item.value ? (
                  <DataGridItem item={item} />
                ) : (
                  <p className={styles.value}>-</p>
                )}
              </div>
            ),
          )}
          {cruiseLineLoyalty && store.isEditMode && (
            <button
              className={classNames(styles.addButton, styles.editIconWrapper)}
              onClick={() => {
                store.setEditMode(false)
                openAddLoyaltyNumberModal()
              }}
            >
              <PlusOutline className={styles.editIcon} />
              <p className={styles.editIconText}>Add loyalty number</p>
            </button>
          )}
        </div>
        {store.isEditMode && (
          <div className={styles.footer}>
            <button
              className={styles.cancelButton}
              onClick={() => {
                store.setEditMode(false)
                reset(defaultFormValues)
              }}
            >
              Cancel
            </button>
            <button
              className={styles.saveButton}
              onClick={submitForm}
              disabled={isSaveDisabled || isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        )}
        {data.length === 0 && emptyStateText && (
          <p className={styles.emptyStateText}>{emptyStateText}</p>
        )}
        {emptyFields.length > 0 && !store.isEditMode && !forceEdit && (
          <p className={styles.errorText}>
            <AlertCircleOutline className={styles.errorIcon} />
            <b>Missing information:</b>{" "}
            {emptyFields.map(
              (field, index) => `${field.label}${index === emptyFields.length - 1 ? "" : ", "}`,
            )}
          </p>
        )}
        {onDataReveal && !store.isEditMode && (
          <button className={styles.dataRevealButton} type="submit" onClick={() => onDataReveal()}>
            Reveal details
          </button>
        )}
      </div>
    )
  },
)
